import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "@me8eon/translation";
import { useJwtToken } from "@me8eon/react_authentication";
import useOnboardingPortalStore from "../OnboardingPortal.Store";
import { EonUiPagination } from "@eon-ui/eon-ui-components-react";
import { useLanguage } from "@me8eon/language";
import OnboardingPortalAppIcon from "../components/OnboardingPortalAppIcon";
import OnboardingPortalTableHeaders from "../components/OnboardingPortalTableHeaders";
import OnboardingPortalStatusFilter from "../components/OnboardingPortalStatusFilter";
import OnboardingPortalSearchFilter from "../components/OnboardingPortalSearchFilter";
import OnboardingPortalLoader from "../components/OnboardingPortalLoader";
import OnboardingPortalRefreshButton from "../components/OnboardingPortalRefreshButton";
import { getCompanyNameFromId } from "../OnboardingPortal.utils";

const DefaultAppActivityScreen = () => {
    const t = useTranslation();
    const jwt = useJwtToken();
    const {
        showModule,
        defaultAppActivity,
        fetchDefaultAppActivity,
        isLoadingDefaultAppActivity,
    } = useOnboardingPortalStore();
    const [lang] = useLanguage();
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("pending");
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const filteredApps = useMemo(
        () =>
            defaultAppActivity.filter((app) => {
                const matchesSearch =
                    search.trim() === ""
                        ? true
                        : app?.app?.title
                              .toLowerCase()
                              .includes(search.toLowerCase()) ||
                          app?.app?.subTitle
                              .toLowerCase()
                              .includes(search.toLowerCase());
                const matchesStatus =
                    status === "all" ? true : app.status === status;
                return matchesSearch && matchesStatus;
            }),
        [search, status, defaultAppActivity]
    );

    useEffect(() => {
        const cp = currentPage;
        const ps = pageSize;
        setCurrentPage(1);
        if (cp > 1 && Math.ceil(filteredApps.length / ps) < cp) {
            setCurrentPage(1);
        }
    }, [filteredApps]);

    useEffect(() => {
        if (showModule === "default-apps") {
            fetchDefaultAppActivity(jwt);
        }
    }, [showModule]);

    return (
        <>
            {defaultAppActivity.length > 0 && (
                <>
                    <div className="onboarding-portal--content--card">
                        {isLoadingDefaultAppActivity && (
                            <OnboardingPortalLoader />
                        )}
                        <div className="onboarding-portal--content--card--header">
                            <div className="onboarding-portal--content--card--header--text">
                                {t(
                                    "onboardingPortal.tableHeaders.defaultAppsActivity"
                                )}{" "}
                                ({filteredApps.length})
                            </div>
                            <div className="onboarding-portal--content--card--header--actions">
                                <OnboardingPortalRefreshButton
                                    onClick={() => fetchDefaultAppActivity(jwt)}
                                />
                                <OnboardingPortalSearchFilter
                                    t={t}
                                    search={search}
                                    setSearch={setSearch}
                                />
                                <OnboardingPortalStatusFilter
                                    t={t}
                                    status={status}
                                    setStatus={setStatus}
                                />
                            </div>
                        </div>
                        <table className="onboarding-portal--table">
                            <OnboardingPortalTableHeaders
                                headers={[
                                    "onboardingPortal.tableHeaders.icon",
                                    "onboardingPortal.tableHeaders.title",
                                    "onboardingPortal.tableHeaders.company",
                                    "onboardingPortal.tableHeaders.update",
                                    "onboardingPortal.tableHeaders.status",
                                ]}
                                t={t}
                            />
                            <tbody>
                                {filteredApps
                                    .slice(
                                        (currentPage - 1) * pageSize,
                                        currentPage * pageSize
                                    )
                                    .map((app) => (
                                        <tr key={app.id}>
                                            <td>
                                                <OnboardingPortalAppIcon
                                                    icon={app?.app?.icon}
                                                    iconUrl={
                                                        app?.app?.iconDataUrl
                                                    }
                                                />
                                            </td>
                                            <td>{app?.app?.title}</td>
                                            <td>
                                                {getCompanyNameFromId(
                                                    app?.company || ""
                                                )}
                                            </td>
                                            <td>
                                                <span
                                                    className={`activity--${app.activity}`}
                                                >
                                                    {t(
                                                        `onboardingPortal.activityLabel.${app.activity}`
                                                    )}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`status--${app.status}`}
                                                >
                                                    {t(
                                                        `onboardingPortal.statusLabel.${app.status}`
                                                    )}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                    <EonUiPagination
                        lang={lang}
                        begin={
                            filteredApps.length === 0
                                ? 0
                                : (currentPage - 1) * pageSize
                        }
                        currentPage={currentPage}
                        end={Math.min(
                            currentPage * pageSize,
                            filteredApps.length
                        )}
                        initialPageSize={pageSize}
                        pageSize={pageSize}
                        pageSizes={"5,10,20,50,100"}
                        totalItems={filteredApps.length}
                        onPageChanged={(e) => {
                            setCurrentPage(e.detail as any);
                            console.log(e.detail);
                        }}
                        onPageSizeChanged={(e) => {
                            setPageSize(e.detail);
                            setCurrentPage(1);
                        }}
                        onPageDecreased={(e) => {
                            console.log(e);
                            setCurrentPage((prev) => Math.max(prev - 1, 1));
                        }}
                        onPageIncreased={(e) => {
                            console.log(e);
                            setCurrentPage((prev) =>
                                Math.min(
                                    prev + 1,
                                    Math.ceil(filteredApps.length / pageSize)
                                )
                            );
                        }}
                    />
                </>
            )}
        </>
    );
};

export default DefaultAppActivityScreen;
